/**
 * =================================================
 * Product Picker Component - v14GAE
 * =================================================
 * * Updated for Schema IProductItem_v04G_Tags
 * * Feature Restore: Added "Clear Filters" button below status text.
 * * Layout:
 * - Top Row: Search | My Selections | Category | Status & Clear Button
 * - Filter Row 1: Category (moved) | Sugar | Caffeine
 * - Filter Row 2: Type (SubCat) | Tags (Marketing)
 * * Version: v14GAE
 * * Date: 2026-01-22
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Container, Row, Col, Card, Button, Table, Form, 
  InputGroup, Toast, ToastContainer, Tooltip, OverlayTrigger, Overlay, Modal, ButtonGroup, ToggleButton
} from 'react-bootstrap';
import { 
  Search, Grid3x3Gap, List as ListIcon, X, 
  Check, ArrowUp, ArrowDown, Clipboard, ChevronDown, ChevronUp, 
  CheckCircleFill, ListOl, ListUl, QuestionCircle, Google, InfoCircleFill, ExclamationTriangleFill, CheckCircle, Trash, ImageFill, Lightbulb
} from 'react-bootstrap-icons';

// IMPORT DATA & COLORS
import { SAMPLE_DATA } from "../ProductPickerData_v04G-Tags";
import { STANDARD_PALETTE, PASTEL_PALETTE, TYPE_BORDERS } from "../ProductPickerColors";

// ==========================================
// 0. SCHEMA DEFINITION (v04G-Tags)
// ==========================================
export type ProductCategory = 'Drink' | 'Snack' | 'Candy';
export type ProductSubCategory = string; 
export type MarketingClass = 'Classic' | 'Healthy' | 'Energy' | 'Sport' | 'Zero Sugar' | 'Special';
export type IntensityLevel = 'None' | 'Low' | 'Med' | 'High';

const ProductImagesURL = "/sundance-images/products/"


export interface IProductItem_v04G_Tags {
  id: string;
  name: string;
  brand: string;
  description: string;

  // Taxonomy
  category: ProductCategory;
  subCategory: ProductSubCategory;

  // Marketing (Now Tags)
  tags: MarketingClass[];

  // Nutrition
  sugarVal: number;
  sugarLevel: IntensityLevel;
  caffeineVal: number;
  caffeineLevel: IntensityLevel;

  // Media
  urlPH: string;
  productImageFileName: string;
}

interface ActiveItem extends IProductItem_v04G_Tags {
  note: string;
}

// ==========================================
// 1. DATA INTEGRITY CHECKER
// ==========================================
const validateDataIds = (data: IProductItem_v04G_Tags[]) => {
    const ids = data.map(item => item.id);
    const uniqueIds = new Set(ids);
    if (ids.length !== uniqueIds.size) {
        const seen = new Set();
        const duplicates = ids.filter(item => {
            if (seen.has(item)) return true;
            seen.add(item);
            return false;
        });
        return { status: 'error', message: `Data Error: Duplicate IDs found: ${duplicates.join(', ')}` };
    }
    return { status: 'ok', message: `Data Integrity Check: OK (${ids.length} unique items)` };
};

// ==========================================
// 2. CLIPBOARD GENERATION
// ==========================================
const generateClipboardText = (
  activeList: ActiveItem[], 
  groupedItems: { drinks: ActiveItem[], snacks: ActiveItem[], candy: ActiveItem[] }, 
  stats: any, 
  copyMode: 'ordered' | 'categorized' | 'both'
) => {
  let output = "--------------------------------\n      SUNDANCE VENDING.COM\nREFRESH | RECHARGE | REVITALIZE\n--------------------------------\n\n";

  const getCatList = (label: string, items: ActiveItem[]) => {
      if(items.length === 0) return "";
      const listText = items.map(i => {
            const noteText = i.note ? ` - Note: ${i.note}` : '';
            return `${i.name} (${i.brand}) [${i.subCategory}]${noteText}`;
      }).join('\n');
      return `\n${label} (${items.length}) -------------\n${listText}\n`;
  };

  const categorizedListText = 
      getCatList("Drinks", groupedItems.drinks) + 
      getCatList("Snacks", groupedItems.snacks) +
      getCatList("Candy", groupedItems.candy);

  const orderedListText = activeList.map((i, idx) => {
      const noteText = i.note ? ` - Note: ${i.note}` : '';
      return `${idx + 1}. ${i.name} (${i.brand})${noteText}`;
  }).join('\n');

  const statsBlock = `
=== PRODUCT METRICS ===
Total Items: ${activeList.length}
Drinks: ${stats.catCounts.Drink}
Snacks: ${stats.catCounts.Snack}
Candy: ${stats.catCounts.Candy}

Avg Sugar: ${stats.avgSugar}g
Avg Caff: ${stats.avgCaff}

Sugar Distribution Item Count
Free: ${stats.sugarCounts.None}
Low: ${stats.sugarCounts.Low}
Med: ${stats.sugarCounts.Med}
High: ${stats.sugarCounts.High}

Caffeine Distribution Item Count
Zero: ${stats.cafCounts.None}
Low: ${stats.cafCounts.Low}
Med: ${stats.cafCounts.Med}
High: ${stats.cafCounts.High}
`.trim();

  if (copyMode === 'categorized' || copyMode === 'both') {
      output += "=== CATEGORIZED LIST ===\n" + categorizedListText;
  }
  
  if (copyMode === 'ordered' || copyMode === 'both') {
      if (output.length > 0 && !output.endsWith('\n\n')) output += "\n\n";
      output += "=== ORDERED LIST ===\n\n" + orderedListText;
  }

  if (copyMode === 'categorized' || copyMode === 'both') {
       output += "\n\n" + statsBlock;
  }

  return output.trim();
};

// ==========================================
// 3. HELP CONFIGURATION
// ==========================================
const HELP_CONFIG = {
    title: "Click for detailed usage instructions",
    helpToggle: "Toggle Help Mode ON/OFF to see tooltips on buttons and features",
    viewGrid: "Switch to Grid (Card) View",
    viewTable: "Switch to Table (List) View",
    search: "Type here to search by Name or Brand",
    clearFilters: "Clear all active filters",
    mySelections: "Show only items currently in your list",
    toggleImages: "Toggle product images ON/OFF",
    
    // Filters
    filterMkt: "Filter products by Marketing Tags (e.g. Healthy, Zero Sugar)",
    filterCat: "Filter products by Category (Drink/Snack/Candy)",
    filterSub: "Filter by specific product type. Options change based on selected Category.",
    filterCaff: "Filter products by Caffeine Level",
    filterSug: "Filter products by Sugar Content",
    
    // Table Headers
    headerName: "Sort by Product Name",
    headerBrand: "Sort by Brand Name",
    headerMktClass: "Sort by Marketing Tags",
    headerCategory: "Sort by Category",
    headerSugar: "Sort by Sugar Content",
    headerCaffeine: "Sort by Caffeine Level",
    
    // Sidebar
    listOrdered: "View list in order of addition (1, 2, 3...)",
    listGrouped: "View list grouped by category (Drinks, Snacks, Candy)",
    copyClipboard: "Copy this list to your clipboard to paste into an email",
    toggleStats: "Show/Hide detailed nutritional statistics",
    addRemove: "Add or Remove this item from your custom list"
};

const HelpWrapper = ({ children, text, helpMode, placement = 'top' }: { children: React.ReactElement, text: string, helpMode: boolean, placement?: any }) => {
    if (!helpMode) return children;
    return (
        <OverlayTrigger placement={placement} overlay={<Tooltip id={`help-${text.substring(0, 10).replace(/\s+/g, '-')}`}>{text}</Tooltip>}>
            <span className="d-inline-block w-100">{children}</span>
        </OverlayTrigger>
    );
};

// ==========================================
// CSS
// ==========================================
const styles = `
  .panel-stat-box {
    background-color: #fff;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 2px rgba(0,0,0,0.02);
  }
  .stat-label { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; color: #adb5bd; margin-bottom: 4px; }
  .stat-value-lg { font-size: 1.8rem; font-weight: 800; color: #212529; line-height: 1; }
  .stat-sub-val { font-size: 0.85rem; font-weight: 600; color: #495057; }
  .dist-row { display: flex; justify-content: space-between; font-size: 0.75rem; padding: 4px 0; }
  .dist-item { text-align: center; flex: 1; }
  .dist-val { font-weight: 700; font-size: 0.9rem; display: block; }
  .dist-lbl { font-size: 0.65rem; color: #868e96; }
  
  .group-header {
    background-color: #f8f9fa;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    padding: 6px 12px;
    border-top: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
    color: #6c757d;
    margin-top: 0px;
  }
  
  .google-link {
    color: #F4B400;
    display: inline-block;
    transition: transform 0.2s;
  }
  .google-link:hover {
    transform: scale(1.1);
    color: #F4B400;
  }
`;

// ==========================================
// Sub-Component: Inactive Note
// ==========================================
const InactiveNote: React.FC = () => {
  const [show, setShow] = useState(false);
  const target = useRef(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (timerRef.current) clearTimeout(timerRef.current);
    setShow(true);
    timerRef.current = setTimeout(() => {
      setShow(false);
    }, 2000);
  };

  return (
    <>
      <div ref={target} onClick={handleClick}>
        <Form.Control
          size="sm"
          as="textarea"
          rows={1}
          placeholder="Notes..."
          value=""
          disabled
          style={{ fontSize: '0.8rem', cursor: 'not-allowed', pointerEvents: 'none', backgroundColor: '#e9ecef' }}
        />
      </div>
      <Overlay target={target.current} show={show} placement="top">
        {(props) => (
          <Tooltip id="overlay-example" {...props}>
            Click card above to add this item to your list & enable notes.
          </Tooltip>
        )}
      </Overlay>
    </>
  );
};

// ==========================================
// Main Component
// ==========================================

interface ToastMsg {
  id: number;
  message: React.ReactNode; 
  variant: 'success' | 'danger' | 'info' | 'warning';
}

// Variables
let productTitle = "Product Picker";
let productSubTitle = "Use this tool to create your own custom product selection."
let productSubTitleVersion = "v14GAE - BETA"
let CopyButtonLabel = "Copy Your Custom List "
let CopyButtonTip = "Click to copy Custom Product List"
let ActiveListTitle = "Custom Product List"
let customListTostMessageTime = 10000;
let VersionNumber = "v14GAE"
let defaultUseImages = true;

let customListTostMessage = (
  <>
    List copied to your clipboard.<br /> 
    Now paste your custom list into your eMail and send to your SunDance Vending representative.
  </>
);

const ProductPicker14GAE: React.FC = () => {
  const [activeList, setActiveList] = useState<ActiveItem[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [searchText, setSearchText] = useState('');
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const [showStats, setShowStats] = useState(false);
  
  const [dataStatus, setDataStatus] = useState<{status: string, message: string}>({status:'ok', message:''});
  const [missingImageCount, setMissingImageCount] = useState<number>(0);

  const [usePastel, setUsePastel] = useState(true);
  const [useLocalImages, setUseLocalImages] = useState(defaultUseImages);
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  const [copyMode, setCopyMode] = useState<'ordered' | 'categorized' | 'both'>('both');
  const [showSelectionsOnly, setShowSelectionsOnly] = useState(false);
  const [helpMode, setHelpMode] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [sidebarViewMode, setSidebarViewMode] = useState<'ordered' | 'grouped'>('grouped');

  // FILTERS UPDATED
  const [filters, setFilters] = useState({
    tags: [] as string[],
    mainCategories: [] as string[],
    subCategories: [] as string[], // NEW
    caffeine: [] as string[],
    sugar: [] as string[]
  });

  const [sortConfig, setSortConfig] = useState<{ key: keyof IProductItem_v04G_Tags; direction: 'asc' | 'desc' } | null>(null);
  const [hoveredProduct, setHoveredProduct] = useState<IProductItem_v04G_Tags | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });


  // CHECKIMAGES - UPDATRED THIS CODE ON 3/4/2026 TO WORK WITH ProductImagesURL that I added instead of hard codeing the path into all the image urls. 
  useEffect(() => {
    const saved = localStorage.getItem('activeList_v14GAE'); 
    if (saved) setActiveList(JSON.parse(saved));
    setDataStatus(validateDataIds(SAMPLE_DATA));

    const checkAllImages = async () => {
        let missingCount = 0;
        const checks = SAMPLE_DATA.map(product => {
            return new Promise<void>((resolve) => {
                if (!product.productImageFileName) {
                    missingCount++;
                    resolve();
                    return;
                }
                const img = new Image();
                img.src = ProductImagesURL + product.productImageFileName;
                img.onload = () => resolve();
                img.onerror = () => {
                    missingCount++;
                    resolve();
                };
            });
        });
        await Promise.all(checks);
        setMissingImageCount(missingCount);
    };
    checkAllImages();
  }, []);

  useEffect(() => {
    localStorage.setItem('activeList_v14GAE', JSON.stringify(activeList));
  }, [activeList]);

  // Updated color helper: Takes Tags and Category. Uses First Tag as primary color.
  const getCurrentColor = (tags: string[], category: string) => {
    const palette = usePastel ? PASTEL_PALETTE : STANDARD_PALETTE;
    const primaryTag = tags.length > 0 ? tags[0] : 'Classic';
    // @ts-ignore
    return palette[primaryTag]?.[category === 'Candy' ? 'Snack' : category] || '#adb5bd';
  };

  const getGoogleSearchUrl = (product: IProductItem_v04G_Tags) => {
    const query = encodeURIComponent(`${product.brand} ${product.name}`);
    return `https://www.google.com/search?tbm=isch&q=${query}`;
  };

  const addToast = (msg: React.ReactNode, variant: 'success' | 'danger' | 'info' | 'warning', duration: number = 3000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message: msg, variant }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  };

  const handleImageError = (id: string) => {
      setImgErrors(prev => ({ ...prev, [id]: true }));
  };

  const toggleFilter = (type: 'tags' | 'caffeine' | 'mainCategories' | 'sugar' | 'subCategories', value: string) => {
    setFilters(prev => {
      const list = prev[type];
      const exists = list.includes(value);
      return { ...prev, [type]: exists ? list.filter(item => item !== value) : [...list, value] };
    });
  };

  const handleCategoryToggle = (val: string) => {
      // When switching main category, clear subcategory filters to avoid confusion
      if (val === 'All') {
          setFilters(prev => ({ ...prev, mainCategories: [], subCategories: [] })); 
      } else {
          setFilters(prev => ({ ...prev, mainCategories: [val], subCategories: [] })); 
      }
  };

  const clearFilters = () => {
    setSearchText('');
    setFilters({ tags: [], caffeine: [], mainCategories: [], sugar: [], subCategories: [] });
    setSortConfig(null);
    setShowSelectionsOnly(false);
    addToast("All filters cleared", "info", 2000);
  };

  const addToActiveList = (product: IProductItem_v04G_Tags) => {
    if (activeList.find(i => i.id === product.id)) return;
    const newItem: ActiveItem = { ...product, note: '' };
    setActiveList(prev => [...prev, newItem]);
    addToast(`${product.name} added to list`, 'success');
  };

  const removeFromActiveList = (id: string) => {
    const item = activeList.find(i => i.id === id);
    setActiveList(prev => prev.filter(i => i.id !== id));
    if (item) addToast(`${item.name} removed from list`, 'danger');
  };

  const updateNote = (id: string, note: string) => {
    setActiveList(prev => prev.map(item => item.id === id ? { ...item, note } : item));
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = Number(e.dataTransfer.getData('text/plain'));
    if (dragIndex === dropIndex) return;
    const newList = [...activeList];
    const [movedItem] = newList.splice(dragIndex, 1);
    newList.splice(dropIndex, 0, movedItem);
    setActiveList(newList);
  };

  const stats = useMemo(() => {
    const total = activeList.length;
    if (total === 0) return { avgSugar: 0, avgCaff: 0, sugarCounts: { None: 0, Low: 0, Med: 0, High: 0 }, cafCounts: { None: 0, Low: 0, Med: 0, High: 0 }, catCounts: { Drink: 0, Snack: 0, Candy: 0 } };
    
    const sumSugar = activeList.reduce((acc, i) => acc + i.sugarVal, 0);
    const sumCaff = activeList.reduce((acc, i) => acc + i.caffeineVal, 0);
    
    const cafCounts = { None: 0, Low: 0, Med: 0, High: 0 };
    const catCounts = { Drink: 0, Snack: 0, Candy: 0 };
    const sugarCounts = { None: 0, Low: 0, Med: 0, High: 0 };
    
    activeList.forEach(i => { 
        if (cafCounts[i.caffeineLevel] !== undefined) cafCounts[i.caffeineLevel]++;
        if (catCounts[i.category] !== undefined) catCounts[i.category]++;
        if (sugarCounts[i.sugarLevel] !== undefined) sugarCounts[i.sugarLevel]++;
    });
    return { avgSugar: (sumSugar / total).toFixed(1), avgCaff: (sumCaff / total).toFixed(1), sugarCounts, cafCounts, catCounts };
  }, [activeList]);

  const groupedItems = useMemo(() => {
    const drinks = activeList.filter(i => i.category === 'Drink');
    const snacks = activeList.filter(i => i.category === 'Snack');
    const candy = activeList.filter(i => i.category === 'Candy');
    return { drinks, snacks, candy };
  }, [activeList]);

  const copyListToClipboard = () => {
    const text = generateClipboardText(activeList, groupedItems, stats, copyMode);
    navigator.clipboard.writeText(text);
    addToast(customListTostMessage, 'warning', customListTostMessageTime);
  };

  const filteredProducts = useMemo(() => {
    let data = [...SAMPLE_DATA];
    
    if (showSelectionsOnly) {
      data = data.filter(p => activeList.some(active => active.id === p.id));
    }

    if (searchText) {
      const lower = searchText.toLowerCase();
      data = data.filter(p => p.name.toLowerCase().includes(lower) || p.brand.toLowerCase().includes(lower));
    }
    
    // FILTER: Tags 
    if (filters.tags.length > 0) {
        data = data.filter(p => p.tags.some(t => filters.tags.includes(t)));
    }

    // FILTER: Caffeine
    if (filters.caffeine.length > 0) {
      data = data.filter(p => filters.caffeine.includes(p.caffeineLevel === 'None' ? 'Zero' : p.caffeineLevel));
    }
    
    // FILTER: Main Category
    if (filters.mainCategories.length > 0) data = data.filter(p => filters.mainCategories.includes(p.category));
    
    // FILTER: Sub Category
    if (filters.subCategories.length > 0) data = data.filter(p => filters.subCategories.includes(p.subCategory));

    // FILTER: Sugar
    if (filters.sugar.length > 0) {
        data = data.filter(p => filters.sugar.includes(p.sugarLevel === 'None' ? 'Zero' : p.sugarLevel));
    }

    if (sortConfig) {
      data.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [searchText, filters, sortConfig, usePastel, showSelectionsOnly, activeList]);

  const activeFiltersDisplay = useMemo(() => {
      const list: string[] = [];
      if (showSelectionsOnly) list.push("My Selections");
      if (searchText) list.push(`Search: "${searchText}"`);
      filters.tags.forEach(c => list.push(c));
      filters.mainCategories.forEach(t => list.push(t === 'Drink' ? 'Drinks' : t === 'Snack' ? 'Snacks' : t));
      filters.subCategories.forEach(s => list.push(s));
      filters.caffeine.forEach(c => list.push(`Caff: ${c}`));
      filters.sugar.forEach(s => list.push(`Sugar: ${s}`));
      return list;
  }, [showSelectionsOnly, searchText, filters]);

  const getCount = (key: keyof IProductItem_v04G_Tags | 'tag', val: string) => {
    if (key === 'caffeineLevel' && val === 'Zero') return SAMPLE_DATA.filter(p => p.caffeineLevel === 'None').length;
    if (key === 'sugarLevel' && val === 'Zero') return SAMPLE_DATA.filter(p => p.sugarLevel === 'None').length;
    if (key === 'tag') return SAMPLE_DATA.filter(p => p.tags.includes(val as any)).length;
    // @ts-ignore
    return SAMPLE_DATA.filter(p => p[key] === val).length;
  };

  // Get available subcategories based on current Main Category selection
  const availableSubCategories = useMemo(() => {
      let subset = SAMPLE_DATA;
      if (filters.mainCategories.length > 0) {
          subset = subset.filter(p => filters.mainCategories.includes(p.category));
      }
      const subs = Array.from(new Set(subset.map(p => p.subCategory))).sort();
      return subs;
  }, [filters.mainCategories]);

  const handleSort = (key: keyof IProductItem_v04G_Tags) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: keyof IProductItem_v04G_Tags) => {
    if (sortConfig?.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>;
  };

  const hasFilters = Boolean(searchText || filters.tags.length || filters.mainCategories.length || filters.subCategories.length || filters.caffeine.length || filters.sugar.length || showSelectionsOnly);

  const renderListItem = (item: ActiveItem, dragIndex: number, displayIndex: number) => (
    <div key={item.id} className="list-group-item d-flex align-items-center justify-content-between p-2" 
         draggable={true} 
         onDragStart={(e) => handleDragStart(e, dragIndex)} 
         onDragOver={handleDragOver} 
         onDrop={(e) => handleDrop(e, dragIndex)} 
         style={{ cursor: 'grab' }}>
      <div className="d-flex align-items-center w-100" style={{ overflow: 'hidden' }}>
         <div className="me-2 flex-shrink-0 d-flex align-items-center justify-content-center bg-light border border-secondary rounded-circle fw-bold text-dark" style={{ width: '28px', height: '28px', fontSize: '0.75rem' }}>
          {displayIndex}
         </div>
         <div className="me-2 flex-shrink-0"><span style={{color: getCurrentColor(item.tags, item.category), fontSize: '1.2rem'}}>●</span></div>
         <div className="text-truncate flex-grow-1" style={{ lineHeight: '1.1' }}>
           <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>{item.name}</div>
           <div className="text-muted small text-truncate">{item.brand} {item.note && <span className="text-info fst-italic ms-1">- "{item.note}"</span>}</div>
         </div>
      </div>
      <div className="ms-2 ps-2 border-start">
          <HelpWrapper text={HELP_CONFIG.addRemove} helpMode={helpMode} placement="left">
            <X size={20} className="text-danger" style={{ cursor: 'pointer' }} onClick={() => removeFromActiveList(item.id)} onMouseEnter={(e) => e.currentTarget.style.cursor = 'default'} />
          </HelpWrapper>
      </div>
    </div>
  );

  return (
    <Container fluid className="p-3 bg-light min-vh-100" style={{ fontSize: '0.9rem' }}>
      <style>{styles}</style>
      
      {/* --- TOP ROW (Adjusted) --- */}
      <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
          <div className="text-muted small">{productSubTitleVersion}</div>
          <div className="d-flex align-items-center">
             {helpMode && (
                <span className="small text-info me-2 animate__animated animate__fadeIn">
                    <InfoCircleFill className="me-1"/>
                    Hover over items on the screen to see description.
                </span>
             )}
             <HelpWrapper text={HELP_CONFIG.helpToggle} helpMode={true} placement="left">
                 <Form.Check 
                    type="switch"
                    id="help-mode-switch"
                    label={<span className={`small fw-bold ${helpMode ? 'text-primary' : 'text-muted'}`}>Help Mode</span>}
                    checked={helpMode}
                    onChange={() => setHelpMode(!helpMode)}
                    className="mb-0"
                 />
             </HelpWrapper>
          </div>
      </div>

      <ToastContainer position="top-center" className="p-3" style={{ zIndex: 9999 }}>
        {toasts.map(t => (
          <Toast key={t.id} bg={t.variant} onClose={() => setToasts(prev => prev.filter(x => x.id !== t.id))}>
            <Toast.Header><strong className="me-auto">Message</strong></Toast.Header>
            <Toast.Body className={t.variant === 'warning' ? 'text-dark fw-bold' : 'text-white'}>{t.message}</Toast.Body>
          </Toast>
        ))}
      </ToastContainer>

      {/* NEW HELP MODAL */}
      <Modal show={showHelp} onHide={() => setShowHelp(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title><QuestionCircle className="me-2 mb-1"/>How to use</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
            <ol className="mb-0" style={{lineHeight: '1.8', fontSize: '1rem'}}>
                <li><strong>Build Your List:</strong> Click on products to add them to your custom list.</li>
                <li><strong>Find Items:</strong> Use the filter, sort, and view tools (Card/Table) to locate products.</li>
                <li><strong>View Options:</strong> Toggle your list between "Ordered" and "Categorized" views using the buttons in the header.</li>
                <li><strong>Add Notes:</strong> Want to add special instructions? Use the notes field on any item in Card View.</li>
                <li><strong>Save & Send:</strong> When you are ready, click <strong>Copy Your Custom List</strong> and paste it into an email to your representative.</li>
                <li><strong>Auto-Save:</strong> Your list saves automatically to your browser, so you can come back and finish it later.</li>
            </ol>
            
            {/* TIPS SECTION */}
            <div className="mt-4 border-top pt-3 bg-light p-3 rounded">
                <h5 className="text-primary"><Lightbulb className="me-2 mb-1 text-warning"/>Tips</h5>
                <ul className="small text-muted mb-0" style={{lineHeight: '1.6'}}>
                    <li className="mb-2"><strong>Views & Tools:</strong> The Grid view is great for browsing, while the Table view helps compare data. Use Sort arrows at the top of each column to help organize your data.</li>
                    <li className="mb-2"><strong>Missing Products?</strong> If you don't see the products you are looking for - be sure to check / clear the current filters that are applied.  If you still don't see the product you are looking for, please eMail your request to <span className="text-primary">ProductRequest@SundanceVending.com</span></li>
                    <li><strong>Google Search:</strong> Click the yellow "G" icon <Google size={12} className="text-warning"/> for a quick google search on that particular product and brand.</li>
                </ul>
            </div>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowHelp(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Row>
        <Col md={9}>
          <Card className="mb-3 shadow-sm">
            <Card.Body>
              <Row className="align-items-center">
                <Col md={5}>
                    <div className="d-flex align-items-center gap-2">
                        <h3 className="mb-0">{productTitle}</h3>
                        <HelpWrapper text={HELP_CONFIG.title} helpMode={helpMode}>
                            <Button variant="link" className="p-0 align-top" onClick={() => setShowHelp(true)}>
                                <QuestionCircle size={22} />
                            </Button>
                        </HelpWrapper>
                    </div>
                  <h5 className="mt-2">{productSubTitle}</h5>
                  {/* <h6>{VersionNumber}</h6> */}
                </Col>
                
                {/* View Controls & Image Toggle (Moved here to balance layout) */}
                <Col md={7} className="text-end">
                  <div className="d-flex justify-content-end align-items-center gap-3">
                      <HelpWrapper text={HELP_CONFIG.toggleImages} helpMode={helpMode}>
                            <Form.Check 
                                type="switch"
                                id="image-toggle"
                                label={<span className="small text-muted fw-normal">Images</span>}
                                checked={useLocalImages}
                                onChange={() => setUseLocalImages(!useLocalImages)}
                                className="mb-0"
                            />
                        </HelpWrapper>
                        <div>
                          <HelpWrapper text={HELP_CONFIG.viewGrid} helpMode={helpMode}>
                            <Button variant={viewMode === 'grid' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('grid')} className="me-2"><Grid3x3Gap /></Button>
                          </HelpWrapper>
                          <HelpWrapper text={HELP_CONFIG.viewTable} helpMode={helpMode}>
                            <Button variant={viewMode === 'table' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('table')}><ListIcon /></Button>
                          </HelpWrapper>
                        </div>
                  </div>
                </Col>
              </Row>
              <hr />
              
              <Row className="g-3 align-items-center">
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text><Search /></InputGroup.Text>
                    <HelpWrapper text={HELP_CONFIG.search} helpMode={helpMode}>
                        <Form.Control placeholder="Filter by name or brand" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                    </HelpWrapper>
                    {hasFilters && (
                        <HelpWrapper text={HELP_CONFIG.clearFilters} helpMode={helpMode}>
                            <Button variant="outline-secondary" onClick={clearFilters} title="Clear all filters">
                                <X size={20} />
                            </Button>
                        </HelpWrapper>
                    )}
                  </InputGroup>
                </Col>
                
                <Col md={2} className="text-center">
                    <HelpWrapper text={HELP_CONFIG.mySelections} helpMode={helpMode}>
                        <div className="p-2 border rounded bg-white shadow-sm d-inline-block" style={{ backgroundColor: showSelectionsOnly ? '#e8f5e9' : 'white' }}>
                            <Form.Check 
                                type="switch"
                                id="show-selections"
                                label={<span className="fw-bold text-success"><CheckCircleFill className="me-1"/> My Selections</span>}
                                checked={showSelectionsOnly}
                                onChange={() => setShowSelectionsOnly(!showSelectionsOnly)}
                                className="m-0"
                            />
                        </div>
                    </HelpWrapper>
                </Col>

                {/* CATEGORY  */}
                  <Col xs={12} md={2}>
                     <div className="p-2 border rounded bg-white shadow-sm h-100">
                        <HelpWrapper text={HELP_CONFIG.filterCat} helpMode={helpMode}>
                            <div className="fw-bold mb-2 text-primary border-bottom pb-1">Category</div>
                        </HelpWrapper>
                        <ButtonGroup size="sm" className="w-100">
                            {['All', 'Drinks', 'Snacks', 'Candy'].map((label, idx) => {
                                const dataValue = label === 'Drinks' ? 'Drink' : label === 'Snacks' ? 'Snack' : label;
                                const isAll = label === 'All';
                                const isActive = isAll ? filters.mainCategories.length === 0 : filters.mainCategories.includes(dataValue);
                                
                                return (
                                    <HelpWrapper text={`Show ${label}`} key={label} helpMode={helpMode}>
                                        <ToggleButton
                                            key={idx}
                                            id={`radio-${idx}`}
                                            type="radio"
                                            variant={isActive ? 'primary' : 'outline-secondary'}
                                            name="radio"
                                            value={dataValue}
                                            checked={isActive}
                                            onChange={() => handleCategoryToggle(dataValue)}
                                        >
                                            {label}
                                        </ToggleButton>
                                    </HelpWrapper>
                                );
                            })}
                        </ButtonGroup>
                     </div>
                  </Col>

                <Col md={4} className="text-end">
                      <div>
                          <span className="text-muted" style={{ fontSize: '1.1rem' }}>
                            Showing <strong>{filteredProducts.length}</strong> of <strong>{SAMPLE_DATA.length}</strong> Products
                          </span>
                          <div className="text-muted mt-1 fw-bold" style={{ fontSize: '1rem' }}>
                              {activeFiltersDisplay.length === 0 ? (
                                  <span>No Filters Applied</span>
                              ) : (
                                  <span className="text-primary">{activeFiltersDisplay.join(' | ')}</span>
                              )}
                          </div>
                          
                          {/* ADDED BACK: Clear Filters Button */}
                          {hasFilters && (
                              <div className="mt-2">
                                <HelpWrapper text={HELP_CONFIG.clearFilters} helpMode={helpMode}>
                                    <Button 
                                        size="sm" 
                                        variant="warning" 
                                        className="fw-bold shadow-sm"
                                        style={{fontSize: '0.8rem', padding: '2px 8px'}}
                                        onClick={clearFilters}
                                    >
                                        Clear Filters <X size={16}/>
                                    </Button>
                                </HelpWrapper>
                              </div>
                          )}
                      </div>
                </Col>
              </Row>
              
              {/* --- FILTER ROW 1: Category | Sugar | Caffeine --- */}
              <Row className="mt-3 border-top pt-3 g-2">
                   {/* Category (Moved from Top Bar) */}

                    {/* Tags (Marketing) */}
                  <Col xs={12} md={5}>
                      <div className="p-2 border rounded bg-white shadow-sm h-100">
                        <HelpWrapper text={HELP_CONFIG.filterMkt} helpMode={helpMode}>
                            <div className="fw-bold mb-2 text-primary border-bottom pb-1">Marketing Tags</div>
                        </HelpWrapper>
                        <div className="d-flex flex-wrap gap-2">
                            {['Classic', 'Healthy', 'Zero Sugar', 'Energy', 'Sport'].map(c => (
                            <HelpWrapper text={`Filter by ${c}`} key={c} helpMode={helpMode}>
                                <Form.Check 
                                    type="checkbox"
                                    id={`cat-${c}`}
                                    label={<span style={{ color: getCurrentColor([c], 'Drink'), fontWeight: '600' }}>{c} ({getCount('tag', c)})</span>}
                                    checked={filters.tags.includes(c)} 
                                    onChange={() => toggleFilter('tags', c)} 
                                />
                            </HelpWrapper>
                            ))}
                        </div>
                      </div>
                  </Col>

                  {/* Sugar */}
                  <Col xs={6} md={4}>
                      <div className="p-2 border rounded bg-white shadow-sm h-100">
                        <HelpWrapper text={HELP_CONFIG.filterSug} helpMode={helpMode}>
                            <div className="fw-bold mb-2 text-primary border-bottom pb-1">Sugar</div>
                        </HelpWrapper>
                        <div className="d-flex flex-wrap gap-2">
                            {['Zero', 'Low', 'Med', 'High'].map(c => (
                            <HelpWrapper text={`Filter by ${c} Sugar`} key={c} helpMode={helpMode}>
                                <Form.Check 
                                    type="checkbox"
                                    id={`sugar-${c}`}
                                    label={`${c} (${getCount('sugarLevel', c)})`} 
                                    checked={filters.sugar.includes(c)} 
                                    onChange={() => toggleFilter('sugar', c)} 
                                />
                            </HelpWrapper>
                            ))}
                        </div>
                      </div>
                  </Col>

                  {/* Caffeine */}
                  <Col xs={6} md={3}>
                      <div className="p-2 border rounded bg-white shadow-sm h-100">
                        <HelpWrapper text={HELP_CONFIG.filterCaff} helpMode={helpMode}>
                            <div className="fw-bold mb-2 text-primary border-bottom pb-1">Caffeine</div>
                        </HelpWrapper>
                        <div className="d-flex flex-wrap gap-2">
                            {['Zero', 'Low', 'Med', 'High'].map(c => (
                            <HelpWrapper text={`Filter by ${c} Caffeine`} key={c} helpMode={helpMode}>
                                <Form.Check 
                                    type="checkbox"
                                    id={`caff-${c}`}
                                    label={`${c} (${getCount('caffeineLevel', c)})`} 
                                    checked={filters.caffeine.includes(c)} 
                                    onChange={() => toggleFilter('caffeine', c)} 
                                />
                            </HelpWrapper>
                            ))}
                        </div>
                      </div>
                  </Col>
              </Row>

              {/* --- FILTER ROW 2: Type | Tags --- */}
              <Row className="mt-2 g-2">
                  
                  {/* Type (SubCategory) */}
                  <Col xs={12} md={12}>
                      <div className="p-2 border rounded bg-white shadow-sm h-100">
                        <HelpWrapper text={HELP_CONFIG.filterSub} helpMode={helpMode}>
                            <div className="fw-bold mb-2 text-primary border-bottom pb-1">
                                Type {filters.mainCategories.length > 0 ? `(${filters.mainCategories[0]})` : '(All)'}
                            </div>
                        </HelpWrapper>

                        <div className="d-flex flex-wrap gap-2" style={{maxHeight: '100px', overflowY: 'auto'}}>
                            {availableSubCategories.map(sub => (
                                <HelpWrapper text={`Filter by ${sub}`} key={sub} helpMode={helpMode}>
                                    <Form.Check 
                                        type="checkbox"
                                        id={`sub-${sub}`}
                                        label={sub}
                                        checked={filters.subCategories.includes(sub)} 
                                        onChange={() => toggleFilter('subCategories', sub)} 
                                        // UPDATED: width: 'auto' allows items to pack tightly based on text length
                                        style={{fontSize: '0.8rem', width: 'auto'}}
                                    />
                                </HelpWrapper>
                            ))}
                        </div>
                      </div>
                  </Col>
              </Row>

            </Card.Body>
          </Card>

          {viewMode === 'grid' && (
            <Row className="g-3">
              {filteredProducts.map(product => {
                const isActive = activeList.some(i => i.id === product.id);
                const activeItem = activeList.find(i => i.id === product.id);
                
                // Color uses first tag or default
                const color = getCurrentColor(product.tags, product.category);
                
                let borderColor = TYPE_BORDERS.Drink;
                if (product.category === 'Snack') borderColor = TYPE_BORDERS.Snack;
                if (product.category === 'Candy') borderColor = TYPE_BORDERS.Candy;

                return (
                  <Col xs={12} sm={6} md={4} lg={2} key={product.id}>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Click card to add to list</Tooltip>}>
                      <Card className="h-100 shadow-sm position-relative" 
                          style={{ 
                              border: `2px solid ${borderColor}`,
                              borderTop: `6px solid ${color}`,
                              cursor: 'pointer' 
                          }} 
                          onClick={() => !isActive && addToActiveList(product)}
                      >
                        {isActive && <div className="position-absolute top-0 start-0 bg-success text-white p-1 rounded-bottom-end" style={{ zIndex: 10 }} onClick={(e) => { e.stopPropagation(); removeFromActiveList(product.id); }}><Check size={20} /></div>}
                        
                        {useLocalImages ? (
                            product.productImageFileName && !imgErrors[product.id] ? (
                                <Card.Img 
                                    variant="top" 
                                    src={ProductImagesURL + product.productImageFileName} 
                                    onError={() => handleImageError(product.id)}
                                    style={{ 
                                        width: '100%', 
                                        height: '200px', 
                                        objectFit: 'contain' 
                                    }} 
                                />
                            ) : (
                                <Card.Img variant="top" src={product.urlPH} style={{ height: '140px', objectFit: 'cover', opacity: '0.3' }} />
                            )
                        ) : (
                            <Card.Img variant="top" src={product.urlPH} style={{ height: '140px', objectFit: 'cover'}} />
                        )}

                        <Card.Body className="p-2">
                          <div className="d-flex justify-content-between align-items-start">
                             <div>
                                <Card.Title style={{ fontSize: '1rem', color }} className="mb-1">{product.name}</Card.Title>
                                <Card.Subtitle className="mb-2 text-muted small">{product.brand}</Card.Subtitle>
                                {useLocalImages && (!product.productImageFileName || imgErrors[product.id]) && (
                                    <div className="text-danger small fw-bold fst-italic mb-2" style={{fontSize: '0.7rem'}}>
                                        Image Coming Soon
                                    </div>
                                )}
                             </div>
                             <OverlayTrigger placement="top" overlay={<Tooltip>Search Google Images for {product.name}</Tooltip>}>
                                <a 
                                    href={getGoogleSearchUrl(product)} 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    className="google-link ms-2"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Google size={18} />
                                </a>
                             </OverlayTrigger>
                          </div>
                          
                          <div className="bg-light p-1 rounded mb-2" style={{fontSize: '0.75rem'}}>
                            <div className="d-flex justify-content-between mb-1">
                                <span className="fw-bold">{product.category}</span>
                                <span className="text-muted">{product.caffeineLevel} Caff</span>
                            </div>
                            <div className="d-flex justify-content-between mb-1">
                                <span>{product.subCategory}</span>
                                <span>Sugar: {product.sugarVal}g</span>
                            </div>
                            {/* TAGS DISPLAY */}
                            <div className="d-flex flex-wrap gap-1 mb-1">
                                {product.tags.map(t => (
                                    <span key={t} className="badge bg-white text-secondary border fw-normal" style={{fontSize:'0.65rem'}}>{t}</span>
                                ))}
                            </div>
                            <div className="text-truncate text-muted">{product.description}</div>
                          </div>

                          <hr className="my-1"/>
                          
                          {isActive ? (
                            <Form.Control
                                size="sm"
                                as="textarea"
                                rows={1}
                                placeholder="Notes..."
                                value={activeItem?.note || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => updateNote(product.id, e.target.value)}
                                style={{ fontSize: '0.8rem' }}
                            />
                          ) : (
                            <InactiveNote />
                          )}

                        </Card.Body>
                      </Card>
                    </OverlayTrigger>
                  </Col>
                );
              })}
            </Row>
          )}

          {viewMode === 'table' && (
            <div className="position-relative">
              <Table hover bordered responsive className="bg-white shadow-sm">
                <thead className="bg-light">
                  <tr>
                    <th className="text-primary" onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                        <HelpWrapper text={HELP_CONFIG.headerName} helpMode={helpMode}>
                            <div className="w-100 d-inline-block">Name {getSortIcon('name')}</div>
                        </HelpWrapper>
                    </th>
                    <th className="text-primary" onClick={() => handleSort('brand')} style={{ cursor: 'pointer' }}>
                        <HelpWrapper text={HELP_CONFIG.headerBrand} helpMode={helpMode}>
                            <div className="w-100 d-inline-block">Brand {getSortIcon('brand')}</div>
                        </HelpWrapper>
                    </th>
                    {/* Tags Column */}
                    <th>
                        <HelpWrapper text={HELP_CONFIG.headerMktClass} helpMode={helpMode}>
                            <div className="w-100 d-inline-block">Tags</div>
                        </HelpWrapper>
                    </th>
                    <th className="text-primary" onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                        <HelpWrapper text={HELP_CONFIG.headerCategory} helpMode={helpMode}>
                            <div className="w-100 d-inline-block">Category {getSortIcon('category')}</div>
                        </HelpWrapper>
                    </th>
                     <th>Sub</th>
                    <th className="text-primary" onClick={() => handleSort('sugarVal')} style={{ cursor: 'pointer' }}>
                        <HelpWrapper text={HELP_CONFIG.headerSugar} helpMode={helpMode}>
                            <div className="w-100 d-inline-block">Sugar {getSortIcon('sugarVal')}</div>
                        </HelpWrapper>
                    </th>
                    <th className="text-primary" onClick={() => handleSort('caffeineLevel')} style={{ cursor: 'pointer' }}>
                        <HelpWrapper text={HELP_CONFIG.headerCaffeine} helpMode={helpMode}>
                            <div className="w-100 d-inline-block">Caff {getSortIcon('caffeineLevel')}</div>
                        </HelpWrapper>
                    </th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(product => {
                    const isActive = activeList.some(i => i.id === product.id);
                    const color = getCurrentColor(product.tags, product.category);
                    
                    return (
                      <tr key={product.id} onMouseEnter={() => setHoveredProduct(product)} onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })} onMouseLeave={() => setHoveredProduct(null)}>
                        <td>
                            {product.name}
                             <OverlayTrigger placement="top" overlay={<Tooltip>Search Google Images for {product.name}</Tooltip>}>
                                <a 
                                    href={getGoogleSearchUrl(product)} 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    className="google-link ms-2"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Google size={16} />
                                </a>
                             </OverlayTrigger>
                        </td>
                        <td>{product.brand}</td>
                        <td style={{ color: color, fontWeight: 'bold' }}>{product.tags.join(', ')}</td>
                        <td>{product.category}</td>
                        <td><small className="text-muted">{product.subCategory}</small></td>
                        <td>{product.sugarVal}g</td>
                        <td>{product.caffeineLevel}</td>
                        <td>
                            <HelpWrapper text={HELP_CONFIG.addRemove} helpMode={helpMode}>
                                <Button 
                                    size="sm" 
                                    variant={isActive ? "outline-danger" : "primary"} 
                                    onClick={() => isActive ? removeFromActiveList(product.id) : addToActiveList(product)}
                                    style={{minWidth: '80px'}}
                                >
                                    {isActive ? <><Trash className="me-1"/>Remove</> : "Add"}
                                </Button>
                            </HelpWrapper>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {hoveredProduct && (
                <Card className="shadow position-fixed" style={{ width: '200px', top: mousePos.y + 10, left: mousePos.x + 10, zIndex: 1050, pointerEvents: 'none' }}>
                   {useLocalImages ? (
                       hoveredProduct.productImageFileName && !imgErrors[hoveredProduct.id] ? (
                           <Card.Img variant="top" src={hoveredProduct.productImageFileName} height="120" style={{objectFit:'contain'}} onError={() => handleImageError(hoveredProduct.id)} />
                       ) : (
                           <Card.Img variant="top" src={hoveredProduct.urlPH} height="120" style={{opacity: '0.5'}} />
                       )
                   ) : (
                       <Card.Img variant="top" src={hoveredProduct.urlPH} height="120" />
                   )}
                   <Card.Body className="p-2 text-center">
                        <h6 className="mb-0" style={{color: getCurrentColor(hoveredProduct.tags, hoveredProduct.category)}}>{hoveredProduct.name}</h6>
                        <small>{hoveredProduct.brand}</small>
                        {useLocalImages && (!hoveredProduct.productImageFileName || imgErrors[hoveredProduct.id]) && (
                            <div className="text-danger x-small fw-bold fst-italic mt-1" style={{fontSize: '0.7rem'}}>Image Coming Soon</div>
                        )}
                   </Card.Body>
                </Card>
              )}
            </div>
          )}
        </Col>

        <Col md={3}>
          <div className="sticky-top" style={{ top: '20px' }}>
            <Card className="shadow-sm border-primary">
              <Card.Header className="bg-primary text-white">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">{ActiveListTitle}</h5>
                    <div className="d-flex gap-1">
                        <HelpWrapper text={HELP_CONFIG.listOrdered} helpMode={helpMode}>
                            <Button size="sm" variant={sidebarViewMode === 'ordered' ? 'light' : 'outline-light'} onClick={() => setSidebarViewMode('ordered')} title="Ordered List" style={{padding:'2px 6px'}}><ListOl/></Button>
                        </HelpWrapper>
                        <HelpWrapper text={HELP_CONFIG.listGrouped} helpMode={helpMode}>
                            <Button size="sm" variant={sidebarViewMode === 'grouped' ? 'light' : 'outline-light'} onClick={() => setSidebarViewMode('grouped')} title="Grouped View" style={{padding:'2px 6px'}}><ListUl/></Button>
                        </HelpWrapper>
                    </div>
                  </div>
                  <HelpWrapper text={HELP_CONFIG.copyClipboard} helpMode={helpMode}>
                    <Button variant="light" size="sm" className="w-100" onClick={copyListToClipboard} title={CopyButtonTip} style={{ padding: '2px 8px' }}>
                        <Clipboard className="me-1"/> {CopyButtonLabel}
                    </Button>
                  </HelpWrapper>
              </Card.Header>
              <Card.Body className="bg-light p-2 small">
                
                <div className="panel-stat-box d-flex justify-content-between align-items-center">
                    <div>
                        <div className="stat-label">Total Items</div>
                        <div className="stat-value-lg">{activeList.length}</div>
                    </div>
                    <div className="text-end">
                        <div className="stat-sub-val text-info">Drinks: {stats.catCounts.Drink}</div>
                        <div className="stat-sub-val text-secondary">Snacks: {stats.catCounts.Snack}</div>
                        <div className="stat-sub-val" style={{color: TYPE_BORDERS.Candy}}>Candy: {stats.catCounts.Candy}</div>
                    </div>
                </div>

                <div className="text-center mb-2">
                    <HelpWrapper text={HELP_CONFIG.toggleStats} helpMode={helpMode}>
                        <Button 
                            variant="link" 
                            size="sm" 
                            onClick={() => setShowStats(!showStats)} 
                            className="text-decoration-none text-muted d-flex align-items-center justify-content-center w-100"
                            style={{fontSize: '0.8rem'}}
                        >
                            {showStats ? <><ChevronUp className="me-1"/> Hide Details</> : <><ChevronDown className="me-1"/> Show Details</>}
                        </Button>
                    </HelpWrapper>
                </div>

                {showStats && (
                    <>
                        <div className="panel-stat-box d-flex justify-content-around animate__animated animate__fadeIn">
                           <div className="text-center">
                              <div className="stat-label">Avg Sugar</div>
                              <div className="stat-value-lg" style={{fontSize: '1.4rem'}}>{stats.avgSugar}g</div>
                           </div>
                           <div style={{borderLeft:'1px solid #dee2e6'}}></div>
                           <div className="text-center">
                              <div className="stat-label">Avg Caff</div>
                              <div className="stat-value-lg" style={{fontSize: '1.4rem'}}>{stats.avgCaff}</div>
                           </div>
                        </div>

                        <div className="panel-stat-box animate__animated animate__fadeIn">
                            <div className="stat-label border-bottom pb-1 mb-2">Sugar Distribution Item Count</div>
                            <div className="dist-row">
                                <div className="dist-item"><span className="dist-val text-success">{stats.sugarCounts.None}</span><span className="dist-lbl">Free</span></div>
                                <div className="dist-item"><span className="dist-val text-success">{stats.sugarCounts.Low}</span><span className="dist-lbl">Low</span></div>
                                <div className="dist-item"><span className="dist-val text-warning">{stats.sugarCounts.Med}</span><span className="dist-lbl">Med</span></div>
                                <div className="dist-item"><span className="dist-val text-danger">{stats.sugarCounts.High}</span><span className="dist-lbl">High</span></div>
                            </div>
                        </div>

                        <div className="panel-stat-box mb-0 animate__animated animate__fadeIn">
                            <div className="stat-label border-bottom pb-1 mb-2">Caffeine Distribution Item Count</div>
                            <div className="dist-row">
                                <div className="dist-item"><span className="dist-val text-secondary">{stats.cafCounts.None}</span><span className="dist-lbl">Zero</span></div>
                                <div className="dist-item"><span className="dist-val text-success">{stats.cafCounts.Low}</span><span className="dist-lbl">Low</span></div>
                                <div className="dist-item"><span className="dist-val text-primary">{stats.cafCounts.Med}</span><span className="dist-lbl">Med</span></div>
                                <div className="dist-item"><span className="dist-val text-danger">{stats.cafCounts.High}</span><span className="dist-lbl">High</span></div>
                            </div>
                        </div>
                    </>
                )}

                <div className="mt-3 pt-2 border-top">
                </div>

              </Card.Body>
              
              <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
                {activeList.length === 0 && <div className="p-3 text-center text-muted">Click items to add to the list.</div>}
                
                {sidebarViewMode === 'ordered' ? (
                    activeList.map((item, index) => renderListItem(item, index, index + 1))
                ) : (
                    <>
                        {groupedItems.drinks.length > 0 && (
                            <>
                                <div className="group-header">Drinks ({groupedItems.drinks.length})</div>
                                {groupedItems.drinks.map((item, i) => {
                                    const globalIndex = activeList.indexOf(item);
                                    return renderListItem(item, globalIndex, i + 1);
                                })}
                            </>
                        )}
                        {groupedItems.snacks.length > 0 && (
                            <>
                                <div className="group-header">Snacks ({groupedItems.snacks.length})</div>
                                {groupedItems.snacks.map((item, i) => {
                                    const globalIndex = activeList.indexOf(item);
                                    return renderListItem(item, globalIndex, i + 1);
                                })}
                            </>
                        )}
                        {groupedItems.candy.length > 0 && (
                            <>
                                <div className="group-header">Candy & Treats ({groupedItems.candy.length})</div>
                                {groupedItems.candy.map((item, i) => {
                                    const globalIndex = activeList.indexOf(item);
                                    return renderListItem(item, globalIndex, i + 1);
                                })}
                            </>
                        )}
                    </>
                )}
              </div>

              <Card.Footer className="text-center text-muted small">
                  Copy and paste this list into your eMail and send to your SunDance Vending representative
              </Card.Footer>
            </Card>
          </div>
        </Col>
      </Row>

      <div className="text-muted small mt-4 text-center border-top pt-2">
        <div>{productSubTitleVersion}</div>
        <div className="d-flex justify-content-center gap-4 mt-1">
            <div className={`fw-bold ${dataStatus.status === 'ok' ? 'text-success' : 'text-danger'}`} style={{fontSize:'0.75rem'}}>
                {dataStatus.status === 'ok' ? <CheckCircle className="me-1"/> : <ExclamationTriangleFill className="me-1"/>}
                {dataStatus.message}
            </div>
            
            <div className={`fw-bold ${missingImageCount === 0 ? 'text-success' : 'text-danger'}`} style={{fontSize:'0.75rem'}}>
                {missingImageCount === 0 ? <CheckCircle className="me-1"/> : <ImageFill className="me-1"/>}
                Images: {missingImageCount > 0 ? `${missingImageCount} Missing/Broken` : 'All OK'}
            </div>
        </div>
      </div>
    </Container>
  );
};

export default ProductPicker14GAE;